import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  createTicker, fetchLeaderboard, fetchTickersAdmin, setTickerFlags, updateTicker,
  TickerValidationError,
  type ModelPerformance, type TickerAdminRow, type TradingMode,
} from "../api/client";

const TRADING_MODE_OPTIONS: { value: TradingMode; label: string; help: string }[] = [
  { value: "full",       label: "Full",       help: "Trade normally — buys + sells." },
  { value: "sells_only", label: "Sells only", help: "Let the model close / reduce existing positions, block new buys. Skips the decision entirely when no position." },
  { value: "disabled",   label: "Disabled",   help: "Skip the ticker every cycle — no data fetch, no decision." },
];

// Soft cap from the UX design choice — anything above this surfaces a
// warning but doesn't block saving. The trainer itself doesn't enforce
// a hard limit; the seed defaults run ~3-5 per ticker.
const COMPETITOR_SOFT_CAP = 5;

/** Format a list of failed-validation entries from the backend into a
 *  user-readable multi-line string. */
function formatValidationError(err: unknown): string {
  if (err instanceof TickerValidationError) {
    return err.invalid
      .map((i) => `${i.ticker}: ${i.error ?? "not found"}`)
      .join("\n");
  }
  return String((err as Error)?.message ?? err);
}

/** Admin page: enable/disable tickers for live trading and backtest, and
 *  see at a glance which model(s) each ticker is currently trading + when
 *  that model was promoted.
 *
 *  Two data sources joined client-side:
 *    - GET /admin/tickers  → enable flags + market_comparisons per ticker
 *    - GET /leaderboard?limit=500 → ModelPerformance rows enriched with
 *      state / pinned / composite_sk / added_at from TradingModels
 *
 *  Disabling `enabled_for_trading` makes the next /list_enabled_tickers
 *  invocation skip the ticker entirely — no more orders generated until
 *  re-enabled. Disabling `enabled_for_backtest` excludes the ticker from
 *  the next TPE run's ticker list.
 */

function relativeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return iso;
  const sec = Math.max(0, (Date.now() - t) / 1000);
  if (sec < 60) return "just now";
  const min = sec / 60;
  if (min < 60) return `${Math.round(min)}m ago`;
  const hr = min / 60;
  if (hr < 24) return `${Math.round(hr)}h ago`;
  const d = hr / 24;
  if (d < 30) return `${Math.round(d)}d ago`;
  const mo = d / 30;
  if (mo < 12) return `${Math.round(mo)}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function StatePill({ state, pinned }: { state: string | null | undefined; pinned: boolean }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    live:       { bg: "#14532d", fg: "#86efac" },
    "paper-live": { bg: "#1e3a8a", fg: "#bfdbfe" },
    retired:    { bg: "#3f3f46", fg: "#a1a1aa" },
  };
  const c = colors[String(state ?? "")] ?? { bg: "#1e293b", fg: "#cbd5e1" };
  return (
    <>
      <span style={{
        background: c.bg, color: c.fg, padding: "2px 6px", borderRadius: 3,
        fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      }}>{state ?? "—"}</span>
      {pinned && (
        <span title="Manually pinned — auto-promote skips this ticker"
              style={{
                marginLeft: 4, background: "#3b0764", color: "#e9d5ff",
                padding: "2px 5px", borderRadius: 3, fontSize: 10, fontWeight: 700,
              }}>📌</span>
      )}
    </>
  );
}

/** Tri-state toggle with confirm-on-change. Keeps the optimistic flip
 *  visible during the mutation so the UI doesn't feel laggy. */
function ToggleSwitch({
  enabled, onToggle, busy, danger,
}: { enabled: boolean; onToggle: () => void; busy: boolean; danger?: boolean }) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      title={enabled ? (danger ? "Click to disable" : "Enabled — click to disable") : "Disabled — click to enable"}
      style={{
        width: 48, height: 24, borderRadius: 12,
        background: enabled ? (danger ? "#16a34a" : "#3b82f6") : "#475569",
        border: "none", cursor: busy ? "wait" : "pointer",
        position: "relative", transition: "background 0.15s",
        opacity: busy ? 0.5 : 1,
      }}
    >
      <span style={{
        position: "absolute",
        top: 2,
        left: enabled ? 26 : 2,
        width: 20, height: 20, borderRadius: 10,
        background: "#f1f5f9", transition: "left 0.15s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      }} />
    </button>
  );
}

/** Inline editor for a comma-separated ticker list. Shows the current
 *  list as chip-like badges. Click "edit" to open a small input + save/
 *  cancel buttons. `softCap` triggers a yellow note when exceeded but
 *  never blocks the save.
 *
 *  `onSave` is a Promise — when it resolves the editor auto-closes; if
 *  it rejects (FMP validation, network, etc.) the editor stays open and
 *  the parent surfaces the failure via `validationError`. */
function TickerListEditor({
  values, onSave, busy, softCap, emptyText, validationError,
}: {
  values: string[];
  onSave: (next: string[]) => Promise<void>;
  busy: boolean;
  softCap?: number;
  emptyText?: string;
  /** A pre-formatted error string from the last save attempt. Shown
   *  inline so users see exactly which symbols FMP rejected. */
  validationError?: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  async function handleSave(parsed: string[]) {
    try {
      await onSave(parsed);
      setEditing(false);
      setDraft("");
    } catch {
      // Keep the editor open so the user can fix typos. The parent
      // populates `validationError` with the FMP failure breakdown.
    }
  }

  if (!editing) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
        {values.length === 0 ? (
          <em style={{ color: "#64748b", fontSize: 12 }}>{emptyText ?? "—"}</em>
        ) : (
          values.map((v) => (
            <code key={v} style={{
              background: "#1e293b", color: "#cbd5e1", padding: "2px 6px",
              borderRadius: 3, fontSize: 11,
            }}>{v}</code>
          ))
        )}
        {softCap !== undefined && values.length > softCap && (
          <span title={`Soft cap is ${softCap}; this is fine but unusual.`}
                style={{ color: "#fbbf24", fontSize: 11 }}>
            ⚠ {values.length} ({">"}{softCap})
          </span>
        )}
        <button
          onClick={() => { setDraft(values.join(", ")); setEditing(true); }}
          disabled={busy}
          style={{
            marginLeft: 4, background: "transparent", color: "#93c5fd",
            border: "1px solid #334155", borderRadius: 3, padding: "1px 6px",
            fontSize: 11, cursor: busy ? "wait" : "pointer",
          }}
        >edit</button>
      </div>
    );
  }

  const parsed = draft
    .split(/[,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="AAPL, MSFT, GOOGL"
        autoFocus
        disabled={busy}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !busy) handleSave(parsed);
        }}
        style={{
          background: "#0b1220",
          color: busy ? "#64748b" : "#e2e8f0",
          border: "1px solid #334155",
          borderRadius: 3, padding: "4px 8px", fontSize: 12, width: "100%",
          fontFamily: "ui-monospace, monospace",
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "wait" : "text",
        }}
      />
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button
          onClick={() => handleSave(parsed)}
          disabled={busy}
          style={{
            background: busy ? "#1e3a8a" : "#2563eb", color: "#fff", border: "none", borderRadius: 3,
            padding: "3px 10px", fontSize: 11, cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >{busy ? "validating…" : "save"}</button>
        <button
          onClick={() => { setEditing(false); setDraft(""); }}
          disabled={busy}
          style={{
            background: "transparent", color: "#94a3b8", border: "1px solid #334155",
            borderRadius: 3, padding: "3px 10px", fontSize: 11,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >cancel</button>
        {softCap !== undefined && parsed.length > softCap && (
          <span style={{ color: "#fbbf24", fontSize: 11 }}>
            ⚠ {parsed.length} symbols ({">"}{softCap} is unusual)
          </span>
        )}
        <span style={{ color: "#64748b", fontSize: 11 }}>
          {parsed.length === 0 ? "(empty)" : `${parsed.length} symbol${parsed.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {validationError && (
        <div style={{ color: "#fca5a5", fontSize: 11, whiteSpace: "pre-line",
                      background: "#1c1917", borderLeft: "3px solid #dc2626",
                      padding: "4px 8px", marginTop: 2 }}>
          {validationError}
        </div>
      )}
    </div>
  );
}


/** Modal for creating a brand-new ticker. Validates client-side that
 *  the ticker is non-empty and uppercase, then submits to POST
 *  /admin/tickers (which re-validates against FMP). */
function AddTickerModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const [ticker, setTicker] = useState("");
  const [benchmarksRaw, setBenchmarksRaw] = useState("SPY");
  const [competitorsRaw, setCompetitorsRaw] = useState("");
  const [trading, setTrading] = useState(false);
  const [backtest, setBacktest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedBenchmarks = benchmarksRaw
    .split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
  const parsedCompetitors = competitorsRaw
    .split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);

  async function handleCreate() {
    setError(null);
    const t = ticker.trim().toUpperCase();
    if (!t) {
      setError("Ticker is required.");
      return;
    }
    setBusy(true);
    try {
      await createTicker({
        ticker: t,
        market_comparisons: parsedBenchmarks,
        competitors: parsedCompetitors,
        enabled_for_trading: trading,
        enabled_for_backtest: backtest,
      });
      onCreated();
    } catch (e) {
      setError(formatValidationError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #334155", borderRadius: 8,
          width: "100%", maxWidth: 520, padding: 20, color: "#e2e8f0",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Add ticker</h3>
        <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 0 }}>
          Creates a new <code>TradingTickers</code> row + matching{" "}
          <code>StockCompetitors</code> row. Every symbol you enter is
          validated against FMP before saving. Defaults to <strong>both
          flags off</strong> — you'll need to enable them explicitly once
          a model is trained + promoted.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 13 }}>Ticker</label>
          <input
            type="text"
            value={ticker}
            onChange={(e) => setTicker(e.target.value)}
            placeholder="NFLX"
            disabled={busy}
            autoFocus
            style={{
              background: "#0b1220", color: "#e2e8f0", border: "1px solid #334155",
              borderRadius: 3, padding: "6px 10px", fontFamily: "ui-monospace, monospace",
              textTransform: "uppercase",
            }}
          />

          <label style={{ fontSize: 13 }}>Benchmarks</label>
          <input
            type="text"
            value={benchmarksRaw}
            onChange={(e) => setBenchmarksRaw(e.target.value)}
            placeholder="SPY, QQQ"
            disabled={busy}
            style={{
              background: "#0b1220", color: "#e2e8f0", border: "1px solid #334155",
              borderRadius: 3, padding: "6px 10px", fontFamily: "ui-monospace, monospace",
            }}
          />

          <label style={{ fontSize: 13 }}>Competitors</label>
          <div>
            <input
              type="text"
              value={competitorsRaw}
              onChange={(e) => setCompetitorsRaw(e.target.value)}
              placeholder="MSFT, GOOGL, AMZN, TSLA, META"
              disabled={busy}
              style={{
                background: "#0b1220", color: "#e2e8f0", border: "1px solid #334155",
                borderRadius: 3, padding: "6px 10px", fontFamily: "ui-monospace, monospace",
                width: "100%",
              }}
            />
            <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
              {parsedCompetitors.length} symbol{parsedCompetitors.length === 1 ? "" : "s"}
              {parsedCompetitors.length > COMPETITOR_SOFT_CAP && (
                <span style={{ color: "#fbbf24", marginLeft: 8 }}>
                  ⚠ above the {COMPETITOR_SOFT_CAP}-peer convention
                </span>
              )}
            </div>
          </div>

          <label style={{ fontSize: 13 }}>Trading</label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}>
            <input type="checkbox" checked={trading} onChange={(e) => setTrading(e.target.checked)} disabled={busy} />
            enabled_for_trading
          </label>

          <label style={{ fontSize: 13 }}>Backtest</label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}>
            <input type="checkbox" checked={backtest} onChange={(e) => setBacktest(e.target.checked)} disabled={busy} />
            enabled_for_backtest
          </label>
        </div>

        {error && (
          <div style={{
            marginTop: 12, color: "#fca5a5", background: "#1c1917",
            borderLeft: "3px solid #dc2626", padding: "8px 12px",
            fontSize: 12, whiteSpace: "pre-line",
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              background: "transparent", color: "#94a3b8", border: "1px solid #334155",
              borderRadius: 4, padding: "8px 16px", cursor: "pointer", fontSize: 13,
            }}
          >Cancel</button>
          <button
            onClick={handleCreate}
            disabled={busy || !ticker.trim()}
            style={{
              background: ticker.trim() && !busy ? "#2563eb" : "#334155",
              color: "#fff", border: "none", borderRadius: 4,
              padding: "8px 16px", fontWeight: 600, fontSize: 13,
              cursor: ticker.trim() && !busy ? "pointer" : "not-allowed",
            }}
          >
            {busy ? "Validating + creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}


export function TickersAdminPage() {
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  // Per-row inline-edit validation error so each TickerListEditor can
  // show what went wrong without polluting the global error banner.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const tickersQ = useQuery({
    queryKey: ["admin-tickers"],
    queryFn: fetchTickersAdmin,
    staleTime: 30_000,
  });
  // Big leaderboard fetch so the join covers every active model. The
  // backend already caches the TradingModels scan for 60s.
  const leaderboardQ = useQuery({
    queryKey: ["leaderboard-for-tickers-admin"],
    queryFn: () => fetchLeaderboard({ limit: 500 }),
    staleTime: 30_000,
  });

  const setFlagsMutation = useMutation({
    mutationFn: (vars: { ticker: string; flags: Parameters<typeof setTickerFlags>[1] }) =>
      setTickerFlags(vars.ticker, vars.flags),
    onSuccess: () => {
      setPendingKey(null);
      setErrorBanner(null);
      queryClient.invalidateQueries({ queryKey: ["admin-tickers"] });
    },
    onError: (err) => {
      setPendingKey(null);
      setErrorBanner(String((err as Error).message || err));
    },
  });

  // Inline list-edit mutation (market_comparisons or competitors). On
  // success we invalidate and clear the row error; on failure we leave
  // the editor open and surface the per-symbol failures so the user can
  // fix typos without retyping the whole list.
  const updateListMutation = useMutation({
    mutationFn: (vars: { ticker: string; field: "market_comparisons" | "competitors"; values: string[] }) =>
      updateTicker(vars.ticker, { [vars.field]: vars.values }),
    onSuccess: (_data, vars) => {
      setPendingKey(null);
      const key = `${vars.ticker}:${vars.field}`;
      setRowErrors((s) => { const next = { ...s }; delete next[key]; return next; });
      queryClient.invalidateQueries({ queryKey: ["admin-tickers"] });
    },
    onError: (err, vars) => {
      setPendingKey(null);
      const key = `${vars.ticker}:${vars.field}`;
      setRowErrors((s) => ({ ...s, [key]: formatValidationError(err) }));
    },
  });

  // Group leaderboard rows by ticker so we can show the active ensemble
  // members + their promotion timestamps inline with each row.
  const modelsByTicker = useMemo(() => {
    const m = new Map<string, ModelPerformance[]>();
    for (const row of leaderboardQ.data?.models ?? []) {
      if (!row.ticker) continue;
      if (!m.has(row.ticker)) m.set(row.ticker, []);
      m.get(row.ticker)!.push(row);
    }
    // Sort each bucket: active ensemble first, then by added_at desc.
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        if (!!a.in_active_ensemble !== !!b.in_active_ensemble) {
          return a.in_active_ensemble ? -1 : 1;
        }
        return String(b.added_at ?? "").localeCompare(String(a.added_at ?? ""));
      });
    }
    return m;
  }, [leaderboardQ.data]);

  const handleToggle = (ticker: string, field: "enabled_for_backtest", current: boolean) => {
    const action = current ? "DISABLE" : "ENABLE";
    const what = "future TPE backtests";
    if (!window.confirm(
      `${action} ${what} for ${ticker}?\n\n` +
      (current ? `Future TPE runs will skip ${ticker}. Existing models are unaffected.\n` : "")
    )) return;
    setPendingKey(`${ticker}:${field}`);
    setFlagsMutation.mutate({ ticker, flags: { [field]: !current } });
  };

  const handleModeChange = (ticker: string, current: TradingMode, next: TradingMode) => {
    if (current === next) return;
    const opt = TRADING_MODE_OPTIONS.find((o) => o.value === next);
    const summary =
      next === "disabled"   ? `The decision engine will stop running for ${ticker} on the next cycle.` :
      next === "sells_only" ? `New buys will be blocked for ${ticker}. The decision engine still runs while a position is open; once flat, it's skipped entirely.` :
      /* full */              `${ticker} will trade normally (buys + sells).`;
    if (!window.confirm(`Set ${ticker} trading mode to "${opt?.label ?? next}"?\n\n${summary}`)) return;
    setPendingKey(`${ticker}:trading_mode`);
    setFlagsMutation.mutate({ ticker, flags: { trading_mode: next } });
  };

  const tickers: TickerAdminRow[] = tickersQ.data?.tickers ?? [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Tickers admin</h2>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            background: "#2563eb", color: "#fff", border: "none",
            borderRadius: 4, padding: "8px 14px", fontWeight: 600,
            fontSize: 13, cursor: "pointer",
          }}
        >+ Add ticker</button>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, maxWidth: 880 }}>
        Per-ticker controls for the trading universe. Toggle{" "}
        <strong style={{ color: "#bfdbfe" }}>Trading</strong> to stop the decision engine
        from generating orders for a ticker (existing positions are unaffected; only NEW
        orders are gated). Toggle <strong style={{ color: "#bfdbfe" }}>Backtest</strong>{" "}
        to exclude the ticker from future TPE runs.{" "}
        <strong style={{ color: "#bfdbfe" }}>Benchmarks</strong> are the broad-market
        comparisons (e.g. SPY); <strong style={{ color: "#bfdbfe" }}>Competitors</strong>{" "}
        are the peer tickers loaded as cross-ticker features for the model.
      </p>

      {showAddModal && (
        <AddTickerModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries({ queryKey: ["admin-tickers"] });
          }}
        />
      )}

      {(tickersQ.isLoading || leaderboardQ.isLoading) && (
        <div className="card" style={{ color: "#94a3b8" }}>Loading…</div>
      )}
      {tickersQ.error && (
        <div className="card" style={{ color: "#fca5a5" }}>
          Tickers load failed: {String((tickersQ.error as Error).message || tickersQ.error)}
        </div>
      )}
      {errorBanner && (
        <div className="card" style={{ color: "#fca5a5", borderLeft: "3px solid #dc2626" }}>
          Update failed: {errorBanner}
        </div>
      )}

      {tickersQ.data && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th style={{ textAlign: "center" }}>Trading mode</th>
                  <th style={{ textAlign: "center" }}>Backtest</th>
                  <th>Benchmarks</th>
                  <th>Competitors</th>
                  <th>Active models · since when</th>
                </tr>
              </thead>
              <tbody>
                {tickers.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "#94a3b8", padding: 20, fontStyle: "italic" }}>
                      No TradingTickers rows. Click <strong>+ Add ticker</strong> to bootstrap one.
                    </td>
                  </tr>
                )}
                {tickers.map((t) => {
                  const models = modelsByTicker.get(t.ticker) ?? [];
                  const active = models.filter((m) => m.in_active_ensemble);
                  const modeBusy = pendingKey === `${t.ticker}:trading_mode`;
                  const backtestBusy = pendingKey === `${t.ticker}:enabled_for_backtest`;
                  const mode: TradingMode = t.trading_mode ?? (t.enabled_for_trading ? "full" : "disabled");
                  const modeLabel = TRADING_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
                  return (
                    <tr key={t.ticker} style={{
                      // De-emphasize tickers that can't open new positions so it's
                      // obvious at a glance what's actually live. "sells_only" and
                      // "disabled" both qualify — sells-only still trades but is
                      // unwinding-only and worth visually distinguishing.
                      opacity: mode === "full" ? 1 : 0.65,
                    }}>
                      <td>
                        <code style={{ fontSize: 13, fontWeight: 700 }}>{t.ticker}</code>
                        {mode === "disabled" && (
                          <div style={{ color: "#fca5a5", fontSize: 11, marginTop: 2 }}>
                            disabled — no decisions, no orders
                          </div>
                        )}
                        {mode === "sells_only" && (
                          <div style={{ color: "#fbbf24", fontSize: 11, marginTop: 2 }}>
                            sells-only — buys blocked
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <select
                          value={mode}
                          disabled={modeBusy}
                          onChange={(e) => handleModeChange(t.ticker, mode, e.target.value as TradingMode)}
                          title={TRADING_MODE_OPTIONS.find((o) => o.value === mode)?.help}
                          style={{
                            background:
                              mode === "full"       ? "#0b1220" :
                              mode === "sells_only" ? "#1f1407" :
                                                       "#1f0707",
                            color:
                              mode === "full"       ? "#86efac" :
                              mode === "sells_only" ? "#fbbf24" :
                                                       "#fca5a5",
                            border: "1px solid #334155",
                            borderRadius: 4,
                            padding: "4px 8px",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: modeBusy ? "wait" : "pointer",
                            opacity: modeBusy ? 0.6 : 1,
                          }}
                          aria-label={`Trading mode for ${t.ticker} (currently ${modeLabel})`}
                        >
                          {TRADING_MODE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <ToggleSwitch
                          enabled={t.enabled_for_backtest}
                          busy={backtestBusy}
                          onToggle={() => handleToggle(t.ticker, "enabled_for_backtest", t.enabled_for_backtest)}
                        />
                      </td>
                      <td style={{ fontSize: 11, color: "#94a3b8", minWidth: 200 }}>
                        <TickerListEditor
                          values={t.market_comparisons}
                          busy={pendingKey === `${t.ticker}:market_comparisons`}
                          emptyText="no benchmarks"
                          validationError={rowErrors[`${t.ticker}:market_comparisons`] ?? null}
                          onSave={async (next) => {
                            setPendingKey(`${t.ticker}:market_comparisons`);
                            await updateListMutation.mutateAsync({
                              ticker: t.ticker, field: "market_comparisons", values: next,
                            });
                          }}
                        />
                      </td>
                      <td style={{ fontSize: 11, color: "#94a3b8", minWidth: 240 }}>
                        <TickerListEditor
                          values={t.competitors}
                          busy={pendingKey === `${t.ticker}:competitors`}
                          softCap={COMPETITOR_SOFT_CAP}
                          emptyText="no competitors"
                          validationError={rowErrors[`${t.ticker}:competitors`] ?? null}
                          onSave={async (next) => {
                            setPendingKey(`${t.ticker}:competitors`);
                            await updateListMutation.mutateAsync({
                              ticker: t.ticker, field: "competitors", values: next,
                            });
                          }}
                        />
                      </td>
                      <td>
                        {active.length === 0 && (
                          <span style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                            No active model — won't trade even if enabled
                          </span>
                        )}
                        {active.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {active.slice(0, 5).map((m) => (
                              <div key={m.model_id} style={{ fontSize: 12, lineHeight: 1.4 }}>
                                <Link to={`/models/${encodeURIComponent(m.model_id)}`}
                                      style={{ color: "#93c5fd", fontFamily: "ui-monospace, monospace" }}>
                                  {m.model_id.length > 32 ? m.model_id.slice(0, 32) + "…" : m.model_id}
                                </Link>
                                <span style={{ marginLeft: 8 }}>
                                  <StatePill state={m.state} pinned={!!m.pinned} />
                                </span>
                                <span title={m.added_at ?? ""}
                                      style={{ marginLeft: 8, color: "#64748b", fontSize: 11 }}>
                                  promoted {relativeAgo(m.added_at)}
                                </span>
                              </div>
                            ))}
                            {active.length > 5 && (
                              <div style={{ color: "#64748b", fontSize: 11 }}>
                                + {active.length - 5} more in ensemble
                              </div>
                            )}
                          </div>
                        )}
                        {/* Show non-active paper-live rows too — these are
                            sitting in TradingModels but not in the latest
                            top-N ensemble. Useful context. */}
                        {models.filter((m) => !m.in_active_ensemble && m.state === "paper-live").length > 0 && (
                          <div style={{ marginTop: 6, color: "#64748b", fontSize: 11 }}>
                            + {models.filter((m) => !m.in_active_ensemble && m.state === "paper-live").length}{" "}
                            paper-live but not in active ensemble
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ background: "#0b1220", borderLeft: "3px solid #3b82f6", fontSize: 12, lineHeight: 1.6, color: "#cbd5e1" }}>
        <strong style={{ color: "#93c5fd" }}>How the controls work:</strong>
        <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
          <li><strong>Trading mode: Full</strong> → trades normally. Default.</li>
          <li><strong>Trading mode: Sells only</strong> → the decision engine still evaluates the ticker while a
            position is open and allows SELL_REDUCE / SELL_SHORT, but any BUY signal is logged with
            <code> no_order_reason="sells_only_mode_blocks_buy"</code> and no order is sent.
            When there's no open position, the ticker is skipped before the data fetch — no decision row.</li>
          <li><strong>Trading mode: Disabled</strong> → <code>list_enabled_tickers</code> filters the
            ticker out of every cycle starting from the NEXT trigger. Live positions stay open (no auto-close).</li>
          <li><strong>Backtest off</strong> → <code>enabled_for_backtest=false</code>. Future TPE runs
            skip this ticker. Existing trained models in TradingModels are untouched.</li>
          <li><strong>📌 Pinned</strong> next to a model = manually promoted from the dashboard. Auto-promote
            will not replace pinned tickers until you unpin from the model detail page.</li>
        </ul>
      </div>
    </div>
  );
}
